import type { MoneyCurrency, MoneyDecimalString } from './money';
import type { AssistedMarketplaceRuntimeCapabilities } from './assistedMarketplaceTypes';

export const SCHEDULED_MARKETPLACE_AGGREGATE_DATASET_CODE =
  'marketplace_inventory_aggregate' as const;
export const SCHEDULED_MARKETPLACE_AGGREGATE_SCHEMA_VERSION =
  'marketplace-inventory-aggregate.v2' as const;

export type MarketJsonPrimitive = string | number | boolean | null;
export type MarketJsonValue =
  MarketJsonPrimitive | MarketJsonValue[] | { [key: string]: MarketJsonValue };

export type MarketDataSourceKind =
  | 'official_publication'
  | 'open_data'
  | 'contracted_feed'
  | 'first_party_marketplace'
  | 'consented_crm_aggregate';

export type MarketDataSourceActivationStatus =
  'candidate' | 'pending_approval' | 'approved' | 'suspended' | 'retired';

export type MarketDataAdapterCode =
  | 'bcrp_series'
  | 'marketplace_aggregate'
  | 'contracted_mortgage_feed'
  | 'consented_crm_aggregate';

export type MarketDataVersionStatus =
  'staged' | 'validating' | 'validated' | 'active' | 'rejected' | 'rolled_back';

export type MarketDataIngestionRunStatus =
  'running' | 'succeeded' | 'failed' | 'rejected';

export type MarketDataAvailabilityStatus =
  | 'available'
  | 'insufficient_sample'
  | 'stale'
  | 'source_unavailable'
  | 'not_covered'
  | 'not_qualified';

export type MarketMetricCode =
  | 'asking_price_per_sqm'
  | 'asking_price_per_sqm_p25'
  | 'asking_price_per_sqm_median'
  | 'asking_price_per_sqm_p75'
  | 'offer_price_index'
  | 'declared_transfer_value_per_sqm'
  | 'verified_closing_price_per_sqm'
  | 'active_inventory'
  | 'observed_inventory'
  | 'listing_velocity_days'
  | 'inventory_age_days'
  | 'price_change_rate'
  | 'mortgage_reference_rate';

export const MARKETPLACE_LISTING_AGGREGATE_METRIC_CODES = [
  'asking_price_per_sqm_p25',
  'asking_price_per_sqm_median',
  'asking_price_per_sqm_p75',
  'observed_inventory',
  'inventory_age_days',
] as const satisfies readonly MarketMetricCode[];

export const COMPANY_MARKET_DATA_CONSENT_METRIC_CODES = [
  ...MARKETPLACE_LISTING_AGGREGATE_METRIC_CODES,
  'verified_closing_price_per_sqm',
  'price_change_rate',
] as const satisfies readonly MarketMetricCode[];

export type MarketValueBasis =
  | 'published_asking_price'
  | 'observed_asking_price'
  | 'offer_index'
  | 'declared_transfer_value'
  | 'verified_closing_price'
  | 'reference_rate'
  | 'lender_quote'
  | 'approved_offer'
  | 'automated_estimate';

export type MarketObservationUnit =
  | 'currency_per_sqm'
  | 'currency'
  | 'count'
  | 'days'
  | 'basis_points'
  | 'percentage';

export interface MarketDataSourceGeographicCoverage {
  countryCode: 'PE';
  pathSlugs: string[];
}

export interface MarketDataSourceQualityChecks {
  allowedAdapterCodes: MarketDataAdapterCode[];
  allowedSeriesCodes: string[];
  minimumAcceptedRows: number;
  maximumRejectedRows: number;
  maximumRejectedRatioBasisPoints: number;
}

export interface MarketDataSource {
  id: string;
  code: string;
  name: string;
  ownerName: string;
  sourceKind: MarketDataSourceKind;
  acquisitionMethod: string;
  sourceUrl: string | null;
  licenseReference: string;
  permittedOutputs: MarketMetricCode[];
  attributionText: string;
  geographicCoverage: MarketDataSourceGeographicCoverage;
  refreshSlaSeconds: number;
  qualityChecks: MarketDataSourceQualityChecks;
  activationStatus: MarketDataSourceActivationStatus;
  approvalReference: string | null;
  approvedByUserId: string | null;
  approvedAt: string | null;
  suspendedReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateMarketDataSourceRequest {
  code: string;
  name: string;
  ownerName: string;
  sourceKind: MarketDataSourceKind;
  acquisitionMethod: string;
  sourceUrl?: string | null;
  licenseReference: string;
  permittedOutputs: MarketMetricCode[];
  attributionText: string;
  geographicCoverage: MarketDataSourceGeographicCoverage;
  refreshSlaSeconds: number;
  qualityChecks: MarketDataSourceQualityChecks;
}

interface IngestBcrpMarketDataSourceRequestBase {
  datasetCode: string;
  adapterCode: 'bcrp_series';
  seriesCodes: string[];
  schemaVersion: string;
}

export interface IngestBcrpPriceMarketDataSourceRequest extends IngestBcrpMarketDataSourceRequestBase {
  metricCode: 'asking_price_per_sqm';
  valueBasis: 'published_asking_price';
  currency: MoneyCurrency;
  unit: 'currency_per_sqm';
  locationPathSlugsBySeriesCode: Record<string, string>;
}

export interface IngestBcrpMortgageMarketDataSourceRequest extends IngestBcrpMarketDataSourceRequestBase {
  metricCode: 'mortgage_reference_rate';
  valueBasis: 'reference_rate';
  currency: MoneyCurrency;
  unit: 'basis_points';
  locationPathSlugsBySeriesCode: Record<string, never>;
}

export type IngestBcrpMarketDataSourceRequest =
  | IngestBcrpPriceMarketDataSourceRequest
  | IngestBcrpMortgageMarketDataSourceRequest;

export interface IngestMarketplaceAggregateSourceRequest {
  datasetCode: string;
  adapterCode: 'marketplace_aggregate';
  schemaVersion: string;
}

export interface IngestContractedMortgageFeedSourceRequest {
  datasetCode: string;
  adapterCode: 'contracted_mortgage_feed';
  schemaVersion: string;
}

export interface IngestConsentedCrmAggregateSourceRequest {
  datasetCode: string;
  adapterCode: 'consented_crm_aggregate';
  schemaVersion: string;
}

export type IngestMarketDataSourceRequest =
  | IngestBcrpMarketDataSourceRequest
  | IngestMarketplaceAggregateSourceRequest
  | IngestContractedMortgageFeedSourceRequest
  | IngestConsentedCrmAggregateSourceRequest;

export interface MarketDataVersion {
  id: string;
  sourceId: string;
  datasetCode: string;
  checksumSha256: string;
  rawSnapshotR2Key: string;
  schemaVersion: string;
  status: MarketDataVersionStatus;
  rowCount: number;
  rejectedRowCount: number;
  periodStart: string | null;
  periodEnd: string | null;
  fetchedAt: string;
  validatedAt: string | null;
  activatedAt: string | null;
  activatedByUserId: string | null;
  rejectionReason: string | null;
  rolledBackAt: string | null;
  createdAt: string;
}

export interface MarketDataIngestionRun {
  id: string;
  sourceId: string;
  datasetCode: string;
  status: MarketDataIngestionRunStatus;
  adapterCode: string;
  sourceRequestUrl: string;
  requestConfig: MarketJsonValue;
  rawSnapshotR2Key: string | null;
  checksumSha256: string | null;
  rowsReceived: number;
  rowsAccepted: number;
  rowsRejected: number;
  errorCode: string | null;
  errorMessage: string | null;
  startedAt: string;
  completedAt: string | null;
}

export interface MarketDataSourceHealth {
  source: MarketDataSource;
  activeVersions: MarketDataVersion[];
  lastRun: MarketDataIngestionRun | null;
  freshnessStatus: 'current' | 'stale' | 'never_ingested';
  staleAfter: string | null;
}

export type CompanyMarketDataConsentStatus = 'granted' | 'revoked';

export interface CompanyMarketDataConsent {
  id: string;
  companyId: string;
  status: CompanyMarketDataConsentStatus;
  consentTextVersion: string;
  permittedMetricCodes: MarketMetricCode[];
  grantedByPrincipalId: string;
  grantedAt: string;
  revokedByPrincipalId: string | null;
  revokedAt: string | null;
  revocationReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface GrantCompanyMarketDataConsentRequest {
  consentTextVersion: typeof COMPANY_MARKET_DATA_CONSENT_VERSION;
  permittedMetricCodes: MarketMetricCode[];
}

export interface MarketDataProvenance {
  sourceId: string;
  sourceCode: string;
  sourceName: string;
  sourceOwner: string;
  sourceUrl: string | null;
  attributionText: string;
  datasetVersionId: string;
  datasetChecksumSha256: string;
  valueBasis: MarketValueBasis;
  lastUpdatedAt: string;
}

export interface MarketDataAvailability {
  status: MarketDataAvailabilityStatus;
  reason: string | null;
  sampleSize: number | null;
  publisherCount: number | null;
  consentingCompanyCount: number | null;
  coverageStart: string | null;
  coverageEnd: string | null;
}

export interface MarketSeriesPoint {
  metricCode: MarketMetricCode;
  periodStart: string;
  periodEnd: string;
  value: string;
  currency: MoneyCurrency | null;
  unit: MarketObservationUnit;
  geographyPathSlug: string;
  availability: MarketDataAvailability;
  provenance: MarketDataProvenance;
}

export interface LocationMarketMetric {
  metricCode: MarketMetricCode;
  value: string | null;
  currency: MoneyCurrency | null;
  unit: MarketObservationUnit;
  periodStart: string | null;
  periodEnd: string | null;
  availability: MarketDataAvailability;
  provenance: MarketDataProvenance | null;
}

export interface LocationMarketSummary {
  locationId: string;
  pathSlug: string;
  displayName: string;
  generatedAt: string;
  metrics: LocationMarketMetric[];
}

export interface LocationMarketSeriesResponse {
  locationId: string;
  pathSlug: string;
  metricCode: MarketMetricCode;
  valueBasis: MarketValueBasis;
  currency: MoneyCurrency | null;
  datasetVersionId: string;
  points: MarketSeriesPoint[];
  availability: MarketDataAvailability;
  truncated: boolean;
  nextBefore: string | null;
}

export interface PublicListingPriceHistoryPoint {
  effectiveAt: string;
  price: MoneyDecimalString;
  currency: MoneyCurrency;
  eventType: 'published' | 'price_changed';
}

export interface ListingComparable {
  publicSlug: string;
  title: string;
  canonicalPath: string;
  price: MoneyDecimalString;
  currency: MoneyCurrency;
  areaM2: string | null;
  locationName: string;
  publicationFreshnessAt: string;
}

export interface ListingMarketContext {
  publicSlug: string;
  publicationFreshnessAt: string;
  askingPriceHistory: PublicListingPriceHistoryPoint[];
  locationSummary: LocationMarketSummary | null;
  comparableActiveListings: ListingComparable[];
  monthlyCostEntryPoint: {
    askingPrice: MoneyDecimalString;
    currency: MoneyCurrency;
    mortgageRatesAvailable: boolean;
  };
  propertyValuation: PropertyValuationResponse | null;
}

export type MortgageRateKind =
  'reference_rate' | 'lender_quote' | 'approved_offer';

export interface MortgageReferenceRate {
  id: string;
  metricCode: 'mortgage_reference_rate';
  rateKind: MortgageRateKind;
  currency: MoneyCurrency;
  annualRateBasisPoints: number;
  lenderName: string | null;
  productName: string | null;
  termMonths: number | null;
  periodStart: string;
  periodEnd: string;
  availability: MarketDataAvailability;
  provenance: MarketDataProvenance;
}

export interface MortgageReferenceRatesResponse {
  generatedAt: string;
  rates: MortgageReferenceRate[];
  history: MortgageReferenceRate[];
  limit: number;
  truncated: boolean;
  nextBefore: string | null;
  disclosure: string;
}

export interface AffordabilityRequest {
  propertyPrice: MoneyDecimalString;
  currency: MoneyCurrency;
  downPayment: MoneyDecimalString;
  annualRateBasisPoints: number;
  termMonths: number;
  monthlyIncome?: MoneyDecimalString;
  monthlyDebt?: MoneyDecimalString;
}

export interface AmortizationScheduleRow {
  installmentNumber: number;
  dueMonth: number;
  openingPrincipal: MoneyDecimalString;
  principal: MoneyDecimalString;
  interest: MoneyDecimalString;
  payment: MoneyDecimalString;
  closingPrincipal: MoneyDecimalString;
}

export interface AffordabilityResult {
  calculationKind: 'illustrative_calculation';
  currency: MoneyCurrency;
  propertyPrice: MoneyDecimalString;
  downPayment: MoneyDecimalString;
  loanPrincipal: MoneyDecimalString;
  monthlyPayment: MoneyDecimalString;
  totalInterest: MoneyDecimalString;
  totalCost: MoneyDecimalString;
  annualRateBasisPoints: number;
  termMonths: number;
  debtToIncomeBasisPoints: number | null;
  schedule: AmortizationScheduleRow[];
  disclosure: string;
}

export type BuyerPlanStage =
  | 'budgeting'
  | 'searching'
  | 'visiting'
  | 'financing'
  | 'ready_to_offer'
  | 'completed';

export interface BuyerPlanItem {
  id: string;
  kind: 'document' | 'visit' | 'financing' | 'task';
  label: string;
  status: 'required' | 'in_progress' | 'completed' | 'not_applicable';
  dueAt: string | null;
  listingPublicSlug: string | null;
  completedAt: string | null;
}

export interface BuyerHomePlan {
  id: string;
  buyerUserId: string;
  stage: BuyerPlanStage;
  currency: MoneyCurrency;
  budget: MoneyDecimalString | null;
  downPayment: MoneyDecimalString | null;
  targetLocationPathSlug: string | null;
  items: BuyerPlanItem[];
  createdAt: string;
  updatedAt: string;
}

export interface UpdateBuyerHomePlanRequest {
  stage: BuyerPlanStage;
  currency: MoneyCurrency;
  budget?: MoneyDecimalString | null;
  downPayment?: MoneyDecimalString | null;
  targetLocationPathSlug?: string | null;
}

export interface UpsertBuyerPlanItemRequest {
  id?: string;
  kind: BuyerPlanItem['kind'];
  label: string;
  status: BuyerPlanItem['status'];
  dueAt?: string | null;
  listingPublicSlug?: string | null;
}

export type MortgageReferralStatus =
  | 'submitted'
  | 'delivered'
  | 'accepted_by_partner'
  | 'declined_by_partner'
  | 'closed';

export interface MortgageReferralPartner {
  id: string;
  code: string;
  legalName: string;
  licenseAuthority: string;
  licenseReference: string;
  gatewayUrl: string;
  status: 'active' | 'suspended';
  supportedCurrencies: MoneyCurrency[];
  createdAt: string;
  updatedAt: string;
}

export type PublicMortgageReferralPartner = Omit<
  MortgageReferralPartner,
  'gatewayUrl'
>;

export interface CreateMortgageReferralPartnerRequest {
  code: string;
  legalName: string;
  licenseAuthority: string;
  licenseReference: string;
  gatewayUrl: string;
  supportedCurrencies: MoneyCurrency[];
}

export interface CreateMortgageReferralRequest {
  partnerId: string;
  propertyPublicSlug?: string | null;
  requestedCurrency: MoneyCurrency;
  requestedAmount: MoneyDecimalString;
  consentTextVersion: typeof MORTGAGE_REFERRAL_CONSENT_VERSION;
  contactEmail: string;
  contactPhone: string;
}

export interface MortgageReferral {
  id: string;
  buyerUserId: string;
  partnerId: string;
  propertyPublicSlug: string | null;
  requestedCurrency: MoneyCurrency;
  requestedAmount: MoneyDecimalString;
  consentTextVersion: string;
  consentedAt: string;
  status: MortgageReferralStatus;
  deliveredAt: string | null;
  partnerReference: string | null;
  createdAt: string;
  updatedAt: string;
}

export type PropertyClaimStatus =
  | 'submitted'
  | 'identity_review'
  | 'ownership_review'
  | 'verified'
  | 'rejected'
  | 'revoked';

export interface PropertyClaim {
  id: string;
  claimantUserId: string;
  realEstateId: string | null;
  propertyGraphNodeId: string | null;
  listingPublicSlug: string | null;
  status: PropertyClaimStatus;
  identityProviderReference: string;
  ownershipEvidenceR2Key: string;
  ownershipEvidenceSha256: string;
  reviewedByUserId: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
  createdAt: string;
  updatedAt: string;
}

interface CreatePropertyClaimRequestBase {
  identityProviderReference: string;
  ownershipEvidenceR2Key: string;
  ownershipEvidenceSha256: string;
}

export type CreatePropertyClaimRequest =
  | (CreatePropertyClaimRequestBase & {
      listingPublicSlug: string;
      propertyGraphNodeId?: never;
    })
  | (CreatePropertyClaimRequestBase & {
      listingPublicSlug?: never;
      propertyGraphNodeId: string;
    });

export type SellerServiceKind =
  | 'valuation_request'
  | 'agent_selection'
  | 'listing_preparation'
  | 'publication';

export interface SellerServiceRequest {
  id: string;
  propertyClaimId: string;
  requesterUserId: string;
  kind: SellerServiceKind;
  status: 'requested' | 'in_progress' | 'completed' | 'cancelled';
  selectedCompanyId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSellerServiceRequest {
  propertyClaimId: string;
  kind: SellerServiceKind;
  selectedCompanyId?: string | null;
}

export interface OwnerDashboard {
  claim: PropertyClaim;
  activeListing: {
    publicSlug: string;
    title: string;
    publishedAt: string;
  } | null;
  performance: {
    impressions: number;
    detailViews: number;
    saves: number;
    comparisons: number;
    inquiries: number;
  };
  marketContext: ListingMarketContext | null;
  sellerServices: SellerServiceRequest[];
}

export type ProfessionalProfileKind = 'agent' | 'developer';

export interface ProfessionalProfileCandidate {
  principalId: string;
  userId: string;
  displayName: string;
}

export interface PublicProfessionalProfile {
  id: string;
  companyId: string;
  userId: string | null;
  kind: ProfessionalProfileKind;
  slug: string;
  displayName: string;
  biography: string | null;
  serviceAreaPathSlugs: string[];
  credentialStatus: 'unverified' | 'verified' | 'expired' | 'revoked';
  responseRateBasisPoints: number | null;
  medianResponseMinutes: number | null;
  transactionCount: number | null;
  averageRatingBasisPoints: number | null;
  reviewCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface ProfessionalCredential {
  id: string;
  profileId: string;
  authorityName: string;
  credentialType: string;
  credentialReference: string;
  validFrom: string;
  validUntil: string | null;
  status: 'pending' | 'verified' | 'expired' | 'revoked';
  verifiedByUserId: string | null;
  verifiedAt: string | null;
}

export interface ProfessionalReview {
  id: string;
  profileId: string;
  reviewerUserId: string;
  transactionId: string;
  ratingBasisPoints: number;
  reviewText: string;
  moderationStatus: 'pending' | 'published' | 'rejected';
  moderatedByUserId: string | null;
  moderatedAt: string | null;
  createdAt: string;
}

export interface EligibleProfessionalReviewTransaction {
  id: string;
  profileId: string;
  propertyTitle: string;
  transactionType: 'sale' | 'rental';
  completedAt: string;
}

export interface PublicProfessionalReview {
  id: string;
  ratingBasisPoints: number;
  reviewText: string;
  createdAt: string;
}

export type ProjectMediaKind =
  | 'floor_plan'
  | 'virtual_tour'
  | 'three_dimensional_tour'
  | 'construction_update';

export interface ProjectMedia {
  id: string;
  companyId: string;
  realEstateId: string;
  unitRealEstateId: string | null;
  kind: ProjectMediaKind;
  title: string;
  providerName: string;
  providerContractReference: string;
  mediaUrl: string | null;
  r2Key: string | null;
  sha256: string | null;
  publishedAt: string;
  createdAt: string;
}

export interface PublicProjectMedia {
  id: string;
  kind: ProjectMediaKind;
  title: string;
  providerName: string;
  mediaUrl: string | null;
  publishedAt: string;
}

export interface RenterProfile {
  id: string;
  buyerUserId: string;
  identityProviderReference: string;
  identityVerifiedAt: string;
  preferredCurrency: MoneyCurrency;
  monthlyIncome: MoneyDecimalString;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateRenterProfileRequest {
  identityProviderReference: string;
  preferredCurrency: MoneyCurrency;
  monthlyIncome: MoneyDecimalString;
}

export interface RentalApplication {
  id: string;
  companyId: string;
  listingPublicSlug: string;
  applicantUserId: string;
  renterProfileId: string;
  status:
    | 'submitted'
    | 'screening_consent_required'
    | 'screening_in_progress'
    | 'approved'
    | 'rejected'
    | 'withdrawn';
  documentConsentVersion: string;
  documentConsentedAt: string;
  screeningConsentGranted: boolean;
  esignatureConsentGranted: boolean;
  paymentConsentGranted: boolean;
  leaseId: string | null;
  screeningProviderReference: string | null;
  screeningRequestStatus: RentalProviderRequest['status'] | null;
  screeningResultReference: string | null;
  screeningCompletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export type RentalApplicationDocumentKind =
  'identity' | 'income' | 'employment' | 'bank_statement' | 'other';

export interface RentalApplicationDocument {
  id: string;
  rentalApplicationId: string;
  documentKind: RentalApplicationDocumentKind;
  sha256: string;
  consentTextVersion: string;
  retentionExpiresAt: string;
  createdAt: string;
}

export interface CreateRentalApplicationRequest {
  listingPublicSlug: string;
  renterProfileId: string;
  documentConsentVersion: string;
}

export interface RentalProviderRequest {
  id: string;
  rentalApplicationId: string;
  attemptNumber: number;
  providerKind: 'screening' | 'identity' | 'esignature' | 'payment';
  providerName: string | null;
  providerRequestReference: string | null;
  status:
    | 'pending_delivery'
    | 'delivery_failed'
    | 'submitted'
    | 'completed'
    | 'failed';
  resultReference: string | null;
  leaseId: string | null;
  paymentCurrency: MoneyCurrency | null;
  paymentAmount: MoneyDecimalString | null;
  paymentDueOn: string | null;
  createdAt: string;
  completedAt: string | null;
}

export interface RentalProviderConsent {
  id: string;
  rentalApplicationId: string;
  providerKind: 'screening' | 'esignature' | 'payment';
  consentTextVersion: string;
  consentedAt: string;
  revokedAt: string | null;
}

export interface RentalLease {
  id: string;
  companyId: string;
  rentalApplicationId: string;
  leaseProviderReference: string | null;
  esignatureRequestStatus: RentalProviderRequest['status'] | null;
  esignatureConsentGranted: boolean;
  paymentConsentGranted: boolean;
  leaseTermsSha256: string;
  status: 'signature_pending' | 'signed' | 'active' | 'ended' | 'cancelled';
  currency: MoneyCurrency;
  monthlyRent: MoneyDecimalString;
  depositAmount: MoneyDecimalString;
  startsOn: string;
  endsOn: string;
  signedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RentPayment {
  id: string;
  companyId: string;
  leaseId: string;
  providerPaymentReference: string;
  currency: MoneyCurrency;
  amount: MoneyDecimalString;
  dueOn: string;
  paidAt: string | null;
  status: 'pending' | 'paid' | 'failed' | 'refunded';
  receiptReference: string | null;
  createdAt: string;
}

export const RENTAL_DOCUMENT_CONSENT_VERSION = 'rental-documents-2026-07-17.v1';
export const RENTAL_SCREENING_CONSENT_VERSION =
  'rental-screening-2026-07-17.v1';
export const RENTAL_ESIGNATURE_CONSENT_VERSION =
  'rental-esignature-2026-07-17.v1';
export const RENTAL_PAYMENT_CONSENT_VERSION = 'rental-payment-2026-07-17.v1';
export const RENTAL_PROVIDER_DOCUMENT_MAX_COUNT = 100;
export const COMPANY_MARKET_DATA_CONSENT_VERSION =
  'market-aggregate-consent-2026-07-24.v2';
export const MORTGAGE_REFERRAL_CONSENT_VERSION =
  'mortgage-referral-2026-07-17.v1';
const BYTES_PER_KIBIBYTE = 1024;
const PRIVATE_DOCUMENT_MAX_MEBIBYTES = 10;
const PRIVATE_DOCUMENT_MULTIPART_OVERHEAD_KIBIBYTES = 64;
export const MARKETPLACE_PRIVATE_DOCUMENT_MAX_BYTES =
  PRIVATE_DOCUMENT_MAX_MEBIBYTES * BYTES_PER_KIBIBYTE * BYTES_PER_KIBIBYTE;
export const MARKETPLACE_PRIVATE_DOCUMENT_MULTIPART_OVERHEAD_BYTES =
  PRIVATE_DOCUMENT_MULTIPART_OVERHEAD_KIBIBYTES * BYTES_PER_KIBIBYTE;
export const MARKETPLACE_PRIVATE_DOCUMENT_CONTENT_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
] as const;
export const VALUATION_MODEL_ALGORITHM_CODE =
  'verified_median_ppsqm_cross_validation_v1';

export interface RentalMaintenanceRequest {
  id: string;
  companyId: string;
  leaseId: string;
  openedByUserId: string;
  category: string;
  description: string;
  status: 'open' | 'scheduled' | 'resolved' | 'closed';
  createdAt: string;
  updatedAt: string;
}

export type PropertyGraphNodeKind = 'parcel' | 'building' | 'property' | 'unit';

export interface PropertyGraphNode {
  id: string;
  kind: PropertyGraphNodeKind;
  canonicalKey: string;
  locationId: string | null;
  parentNodeId: string | null;
  addressFingerprintSha256: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PropertyGraphEdge {
  id: string;
  fromNodeId: string;
  toNodeId: string;
  relationType: 'contains' | 'part_of' | 'same_as_candidate' | 'supersedes';
  status: 'candidate' | 'verified' | 'rejected';
  evidenceSourceId: string | null;
  reviewedByUserId: string | null;
  reviewedAt: string | null;
  createdAt: string;
}

export interface PropertyGraphRealEstateLink {
  id: string;
  propertyGraphNodeId: string;
  companyId: string;
  realEstateId: string;
  status: 'candidate' | 'verified' | 'rejected';
  linkedByUserId: string;
  linkedAt: string;
}

export interface PublicPropertyRecord {
  id: string;
  propertyGraphNodeId: string;
  sourceId: string;
  sourceRecordReference: string;
  recordKind: 'registry' | 'notary' | 'cadastre';
  effectiveAt: string;
  payload: MarketJsonValue;
  datasetVersionId: string;
  createdAt: string;
}

export interface ValuationModelVersion {
  id: string;
  code: string;
  version: string;
  algorithmCode: typeof VALUATION_MODEL_ALGORITHM_CODE;
  modelArtifactSha256: string;
  propertySegment: string;
  geographyPathSlug: string;
  trainingWindowStart: string;
  trainingWindowEnd: string;
  verifiedObservationCount: number;
  holdoutMapeBasisPoints: number;
  p90ErrorBasisPoints: number;
  confidenceCalibrationBasisPoints: number;
  evidenceSnapshotSha256: string | null;
  status: 'candidate' | 'qualified' | 'retired';
  qualifiedAt: string | null;
  createdAt: string;
}

export interface MarketGeographyQualification {
  id: string;
  geographyPathSlug: string;
  propertySegment: string;
  sourceCoveragePassed: boolean;
  listingDensityPassed: boolean;
  operationalSupportPassed: boolean;
  valuationModelVersionId: string | null;
  status: 'candidate' | 'qualified' | 'suspended';
  reviewedByUserId: string;
  reviewedAt: string;
  reviewNote: string;
  createdAt: string;
  updatedAt: string;
}

export interface PropertyValuation {
  id: string;
  propertyGraphNodeId: string;
  modelVersionId: string;
  currency: MoneyCurrency;
  estimate: MoneyDecimalString;
  confidenceLow: MoneyDecimalString;
  confidenceHigh: MoneyDecimalString;
  effectiveAt: string;
  evidenceObservationCount: number;
  inputAreaM2: string;
  modelArtifactSha256: string;
  inputSnapshotSha256: string;
  limitations: string[];
  createdAt: string;
}

export interface PropertyValuationResponse {
  availability: MarketDataAvailability;
  valuation: PropertyValuation | null;
  model: ValuationModelVersion | null;
}

export interface MarketplaceProgramCapabilities {
  assistedMarketplace: AssistedMarketplaceRuntimeCapabilities;
  marketData: boolean;
  mortgageReferenceRates: boolean;
  mortgagePartnerProducts: boolean;
  mortgageReferrals: boolean;
  ownerClaims: boolean;
  professionalProfiles: boolean;
  renterProfiles: boolean;
  rentalScreening: boolean;
  rentalESignature: boolean;
  rentalPayments: boolean;
  automatedValuations: boolean;
  reasons: Partial<
    Record<
      | 'marketData'
      | 'mortgageReferenceRates'
      | 'mortgagePartnerProducts'
      | 'mortgageReferrals'
      | 'ownerClaims'
      | 'professionalProfiles'
      | 'renterProfiles'
      | 'rentalScreening'
      | 'rentalESignature'
      | 'rentalPayments'
      | 'automatedValuations',
      string
    >
  >;
}
